import { IconButton, InputAdornment, MenuItem, TextField } from '@mui/material';
import React from 'react';
import { AppLoadingMoreMenuItem } from './AppLoadMoreMenuItem';
import { AppInputSearchMenuItem } from './AppInputSearchMenuItem';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { FIELD_LENGTH } from 'utils/constants';
import './../TextField/TextField.scss';

const AppInput = <T extends Record<string, any>>(props: IAppInputProps<T>) => {
    const {
        value,
        onSearch,
        defaultValue,
        placeHolder,
        register,
        name,
        rules,
        setValue,
        options,
        menuItem,
        loadMore,
        isVisibleLoadMore = true,
        password,
        onDropdownChange,
        onBlur,
        onKeyDown,
        maxLength,
        watch,
        unit,
        callBack,
        disabled,
        sx,
        multiline,
        inputSlotProps,
        minRows,
        maxRows,
        showPlaceholder = true,
        formHelperTextProps,
        ...rest
    } = props;

    const [showPassword, setShowPassword] = React.useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (setValue) {
            setValue(name, event.target.value.trim() ?? '', {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
        if (onDropdownChange) {
            onDropdownChange(event.target.value);
        }
    };
    const handleTrimOnBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (setValue && props.select === undefined) {
            const trimmedValue = event.target.value.trim();
            setValue(name, trimmedValue, { shouldValidate: false });
        }
    };

    return (
        <TextField
            {...rest}
            {...(props.select && watch ? { value: watch(name) ?? '' } : {})}
            onKeyDown={onKeyDown}
            {...(props.select ? { defaultValue } : {})}
            {...register(name, rules)}
            {...(!props.select && { onBlur: onBlur ? onBlur : (event) => handleTrimOnBlur(event) })}
            fullWidth
            placeholder={placeHolder}
            type={password ? (showPassword ? 'text' : 'password') : undefined}
            multiline={multiline}
            minRows={minRows}
            maxRows={maxRows}
            slotProps={{
                inputLabel: {
                    shrink: true,
                    sx: {
                        color: 'var(--color-49454f)',
                        fontSize: '18px',
                        fontWeight: '400',
                        background: 'white',
                        paddingRight: 1,
                    },
                },
                htmlInput: {
                    maxLength:
                        name === 'comment' || name === 'jwnet_info'
                            ? FIELD_LENGTH.comment
                            : maxLength,
                },
                input: {
                    readOnly: disabled,
                    onClick: (e) => {
                        if (callBack) callBack(e);
                    },
                    ...(password && {
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle pwd"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    onMouseUp={handleMouseUpPassword}
                                    edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }),
                    ...(unit && {
                        endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                    }),
                    ...inputSlotProps
                },
                formHelperText: {
                    ...(formHelperTextProps ?? {}),
                },
            }}
            {...(props.select && { onChange })}
            {...(props.select && loadMore
                ? { SelectProps: { MenuProps: { PaperProps: { style: { maxHeight: 300 } } } } }
                : {})}
            sx={[
                {
                    '& .MuiFormLabel-asterisk': {
                        color: '#d32f2f',
                        fontSize: '20px',
                    },
                    '& .MuiInputBase-input': {
                        fontSize: '16px',
                    },
                    ...(props.select
                        ? {
                              '& .MuiFormLabel-root': {
                                  color: rest.error ? '#d32f2f' : 'var(--color-49454f)',
                              },
                              '& .MuiSelect-select span::before': {
                                  content: `'${placeHolder || ''} '`,
                                  color: '#999999',
                              },
                              '& .MuiInputLabel-shrink': {
                                  paddingRight: 1,
                                  background: 'white',
                              },
                              '& .Mui-disabled': {
                                  color: '#BDBDBD',
                              },
                          }
                        : {}),
                },
                sx, 
            ]}
            className="hide-password-reveal">
            {options && onSearch && <AppInputSearchMenuItem onSearch={(txt) => onSearch(txt)} />}
            {!!(props.select && !props.required && options && showPlaceholder) && (
                <MenuItem
                    value=""
                    sx={{
                        color: '#999999',
                    }}>
                    {placeHolder}
                </MenuItem>
            )}
            {options &&
                options.map((option) =>
                    menuItem ? (
                        menuItem(option)
                    ) : (
                        <MenuItem key={`${option.value}`} value={`${option.value}`}>
                            {option.label}
                        </MenuItem>
                    ),
                )}
            {isVisibleLoadMore && options && loadMore && (
                <AppLoadingMoreMenuItem loadMore={loadMore} />
            )}
        </TextField>
    );
};

export default AppInput;
